<template>
  <aw-modal
    size="sm"
    :opened="opened"
    :closable="closable"
    :modal-title="$awt('aw.cold_parcel_expire_timeframe.expired_title')"
    @close-modal="closeModal"
  >
    <template #content>
      <aw-separator margin-top="none" />
      <aw-notification-item
        :notification="{
          type: 'error',
          iconName: 'error-16',
          manualClose: false,
          text: {
            subtitle: $awt('aw.cold_parcel_expire_timeframe.expired_description')
          }
        }"
        index="0"
        thick-content
        is-margin-less
      />

      <div v-if="mappedTimeframes.length > 0" :class="$style.timeframeSelection">
        <aw-select-version2
          v-model:selected-options="selectedTimeframe"
          tabindex="0"
          :class="$style.timeframeSelector"
          :inline-label-text="$awt('aw.cold_parcel_expire_timeframe.choose_time_frame')"
          :multiple="false"
          dynamic-width
          :options="mappedTimeframes"
          :dropdown-icon-attrs="{
            name: 'arrow-down-16',
            size: 14
          }"
        />
      </div>
      <aw-separator />
      <div :class="$style.buttonContainer">
        <lv-button
          styling="primary-dark"
          shrink
          :disabled="selectedTimeframe.id === DEFAULT_TIMEFRAME_ID"
          loading-id="expired-cold-parcel-timeframe-save"
          @click="saveTimeframe"
        >
          {{ $awt('aw.cold_parcel_expire_timeframe.save_another_timeframe') }}
        </lv-button>
        <lv-button styling="secondary-dark" shrink @click="closeModal">
          {{ $awt('aw.cold_parcel_expire_timeframe.close') }}
        </lv-button>
      </div>
    </template>
  </aw-modal>
</template>
<script setup>
  import { defineAsyncComponent, ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useNuxtApp } from 'nuxt/app';
  import { M_COLD_PARCEL_DELIVERY_TIMEFRAME_EXPIRED } from '~~/common/config/Modal';
  import { LvButton } from '~~/common/components/loginet-vue-shop/index';
  import AwModal from '~~/common/components/Common/AwModal';
  import AwSeparator from '~~/common/components/Common/AwSeparator';

  import { DEFAULT_TIMEFRAME_ID, formatTimeframesForSelect, saveSelectedTimeframe } from '~~/common/utils/timeframeHelper';
  import { useModalStore } from '~~/common/stores/modal';
  import { useCheckoutStore } from '~~/common/stores/checkout';
  import { useDeliveryStore } from '~~/shop/app/stores/delivery';
  import { DM_DELIVERY_COOLOMAT_LOCKER } from '~~/common/config/DeliveryMethod';

  const checkTimeFrameIsExpiredTimeout = 20000;

  const {
    $awt,
    $date,
    $logger,
  } = useNuxtApp();

  // Components
  const AwSelectVersion2 = defineAsyncComponent(() => import('~~/common/components/Common/AwSelectVersion2'));
  const AwNotificationItem = defineAsyncComponent(() => import('~~/common/components/Common/AwNotificationItem.vue'));

  // Stores
  const modalStore = useModalStore();
  const deliveryStore = useDeliveryStore();
  const checkoutStore = useCheckoutStore();

  // Reactive refs from stores
  const {
    timeframes,
    expiredColdParcelTimeframeVisible: expiredVisible,
    coolomatId,
  } = storeToRefs(deliveryStore);
  const { activeGenericModal: modal } = storeToRefs(modalStore);
  const { getFoodCheckout: checkout } = storeToRefs(checkoutStore);

  // Local reactive state
  const expired = ref(null);
  const expireCheckInterval = ref(null);
  const selectedTimeframe = ref(null);

  // Computed properties
  const opened = computed(() => modal.value.type === M_COLD_PARCEL_DELIVERY_TIMEFRAME_EXPIRED);
  const closable = computed(() => modal.value.item.closable);
  const reservation = computed(() => checkout.value?.reservation);
  const checkoutDelivery = computed(() => checkout.value?.delivery);

  const mappedTimeframes = computed(() => {
    return formatTimeframesForSelect(
      timeframes.value,
      $awt,
      $date,
      'aw.cold_parcel_expire_timeframe.choose_time_frame',
    );
  });

  // Watchers
  watch(mappedTimeframes, (newTimeframes) => {
    if (newTimeframes.length > 0 && !selectedTimeframe.value) {
      selectedTimeframe.value = newTimeframes[0]; // Set to default option
    }
  }, { immediate: true });

  // Methods
  const closeModal = async () => {
    modalStore.closeColdParcelExpireTimeFrameModal();
    if (expired.value === true) {
      deliveryStore.setExpiredColdParcelTimeframeVisible(false);
    }
    // Reset selected timeframe when closing modal
    selectedTimeframe.value = null;
    clearInterval(expireCheckInterval.value);
    expireCheckInterval.value = setInterval(checkStatus, checkTimeFrameIsExpiredTimeout);
  };

  const checkStatus = async () => {
    if (reservation.value && !reservation.value.original && checkoutDelivery.value?.deliveryMethod === DM_DELIVERY_COOLOMAT_LOCKER) {
      const expireDate = new Date(reservation.value.expiresAt);
      const currentDate = new Date();

      if (!expiredVisible.value && expireDate < currentDate) {
        expired.value = true;
        await getTimeframes();
        modalStore.openColdParcelExpireTimeFrameModal();
      }
    }
  };

  const getTimeframes = async () => {
    await deliveryStore.fetchTimeframes(coolomatId.value ? { coolomatId: coolomatId.value } : null)
      .catch((e) => {
        $logger.error(e);
      });
  };

  async function saveTimeframe () {
    await saveSelectedTimeframe(selectedTimeframe.value, timeframes.value, 'expired-cold-parcel-timeframe-save', closeModal);
  }


  // Lifecycle hooks
  onMounted(async () => {
    await checkStatus();
    expireCheckInterval.value = setInterval(checkStatus, checkTimeFrameIsExpiredTimeout);
  });

  onBeforeUnmount(() => {
    clearInterval(expireCheckInterval.value);
  });
</script>

<style module lang="scss" rel="stylesheet/scss">
.timeframeModal {
  &Heading {
    padding-bottom: 24px;
    color: $color-brand-secondary;
  }

  &Close {
    padding-top: 32px;
    text-align: center;
  }
}

.timeframeSelection {
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  padding-top: 20px;
  padding-bottom: 0;

  @include tablet(min) {
    padding-top: 40px;
  }

  .timeframeSelector {
    width: max-content;
  }
}

.buttonContainer {
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  gap: 16px;

  @include tablet(min) {
    flex-direction: row;
  }
}
</style>
